import { FC, useCallback, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import { CheckCircleIcon, Cluster, LocationIcon, Separator, Stack, Typography } from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { AddressForm, AddressFormField } from './AddressForm'
import { locationsQueries } from '../api/locations.queries'
import { useAddressSelectorContext } from '../contexts/useAddressSelectorContext'
import { useAddressSelectorDispatchContext } from '../contexts/useAddressSelectorDispatchContext'
import { AddressFormData } from '../schema'
import { mapLocationResponseV1ToAutoCompleteAddressFormData } from '../utils/addressDetailMapper'

export const AddressDetail: FC = () => {
  const { addressFromSearch, value, error, warning } = useAddressSelectorContext()
  const { onSearchAddressResultSelected } = useAddressSelectorDispatchContext()
  const { translate } = useLocalisation()

  const { localAddress: localAddressError } = (error ?? {}) as Record<
    string,
    Record<AddressFormField, { message: string }>
  >

  const { isLoading, data } = useQuery({
    ...locationsQueries.detail(addressFromSearch?.locationRef as string),
    enabled: !!addressFromSearch?.locationRef,
  })

  useEffect(() => {
    if (!data) {
      return onSearchAddressResultSelected(null)
    }

    onSearchAddressResultSelected(mapLocationResponseV1ToAutoCompleteAddressFormData(data))
  }, [data, onSearchAddressResultSelected])

  const onLocalAddressChange = useCallback(
    (field: AddressFormField, fieldValue: string) => {
      const autoCompleteAddress = value as AddressFormData

      onSearchAddressResultSelected({
        ...autoCompleteAddress,
        localAddress: {
          ...(autoCompleteAddress?.localAddress ?? {}),
          [field]: fieldValue,
        },
      })
    },
    [onSearchAddressResultSelected, value]
  )

  if (!isLoading && !value) {
    return null
  }

  return (
    <Stack gap={16}>
      <Cluster aria-label={translate('locations.address_detail')} gap={8} y="center">
        <LocationIcon />
        <Stack gap={4}>
          <Typography variant="small" color="positive">
            <Cluster y="center" gap={4}>
              {translate('locations.verified_address')}
              <CheckCircleIcon size="xs" color="positive" />
            </Cluster>
          </Typography>
          <Typography variant="body">{isLoading ? translate('locations.loading') : value?.addressFormatted}</Typography>
          {!!warning && (
            <Typography variant="small" color="warning">
              {warning}
            </Typography>
          )}
        </Stack>
      </Cluster>
      {!!value?.localAddress && (
        <>
          <Separator orientation="horizontal" />
          <AddressForm
            value={value.localAddress}
            title={translate('locations.search_address_form.local_header')}
            onChange={onLocalAddressChange}
            disabledFields={['countryCode']}
            aria-label={translate('locations.search_address_form.local_section')}
            error={localAddressError}
          />
        </>
      )}
    </Stack>
  )
}
